import { useCallback, useReducer } from "react";
import { BundleTypes, generateRandomUniqueId } from "../constants";
import { BxgyConfigInterface, Action, ChangeEvents } from "../Types";

export const initialConfigData: BxgyConfigInterface = {
    status: false,
    bundle_type: BundleTypes.BXGY,
    bundle_as_product: {
        product_title: "",
        product_description: "",
        images: [],
        show_on_product_page: [],
        product_handle: "",
        double_discount: false,
        collections: [],
    },
    allowed_product_pages: [],
    render_product_pages_type: false,
    isProductPageVisible: false,
    show_product_quantity: true,
    discounted_variant_mode: "customer_chooses",
    // design from
    bundles_design: {
        default_cta: true,
        bundle_title_size: 20,
        bundle_title_color: "#000000",
        bundle_title_style: "550 normal",
        product_name_size: 14,
        product_name_color: "#050505",
        product_name_style: "450 normal",
        product_price_size: 10,
        product_price_color: "#050505",
        product_price_style: "350 normal",
        border_color: "#050505",
        border_width: 2,
        border_radius: 12,
        background_color: "#F2F4F2",
        highlighted_offer_tag_size: 12,
        highlighted_offer_tag_color: "#F8F8F8",
        highlighted_offer_tag_style: "550 italic",
        highlighted_price_tag_size: 14,
        highlighted_price_tag_color: "#050505",
        highlighted_price_tag_style: "650 normal",
        total_footer_text_size: 14,
        total_footer_text_color: "#000000",
        total_footer_text_style: "550 normal",
        total_footer_price_text_size: 16,
        total_footer_price_text_color: "#000000",
        total_footer_price_text_style: "750 normal",
        total_footer_background_color: "#ebeaea",
        total_footer_background_style: "450 normal",
        product_background_color: "#FFFFFF",
        product_border_color: "#303030",
        add_to_cart_bg_color: "#303030",
        add_to_cart_border_radius: 8,
        add_to_cart_border_color: "#FFFFFF",
        add_to_cart_border_width: 0,
        add_to_cart_text_color: "#F5F5F5",
        add_to_cart_text_size: 14,
        add_to_cart_text_style: "750 normal",
        special_text_background_color: "#000000",
        special_text_color: "#FFFFFF",
        special_text_font_size: 10,
        special_text_style: "650 normal",
        bundle_widget_bg: "#f4f4f400",
        bundle_widget_font_family: "Custom Store Font",
        product_img_size: "medium",
        product_img_show: "all",
        product_img_border_radius: 8,
        product_img_border_width: 1,
        product_img_border_color: "#cccccc",
        variant_selector_text_size: 12,
        variant_selector_text_color: "#383838",
        variant_selector_text_style: "450 normal",
        free_gift_header_bg_color: "#d5d5d5",
        free_gift_header_color: "#303030",
        free_gift_header_size: 13,
        free_gift_header_style: "650 normal",
        free_gift_bg_color: "#eaeaea",
        free_gift_text_color: "#303030",
        free_gift_text_size: 12,
        free_gift_text_style: "650 normal",
        free_gift_border: true,
        free_gift_border_width: 0,
        free_gift_border_color: "#441144",

        footer_save_money_text_size: 16,
        footer_save_money_text_style: "550 normal",
        footer_save_money_text_color: "#030303",
        swatches_select_color: "#000000",
    },
    // content form
    bundle_name: "",
    title: "Bundle and Save more",
    description: "",
    discount_type: "percentage",
    discount_value: 5,
    order_discounts: true,
    product_discounts: true,
    selected_template_bxgy: "",
    shipping_discounts: true,
    products: [],
    volume_discounts: [],
    discounted_products: [],
    bundle_product_level: "product",
    hide_native_elements: "",
    add_to_cart_cta_text: "Add to Cart",
    y_label_get_text: "Get",
    y_label_off_text: "Off",
    free_text: "Free",
    x_label_buy_text: "Buy",
    quantity_text: "Qty",
    footer_total_text: "Total",
    offer_name: "",
    discount_label_template: "Bundle and Save more",
    should_flatten_products: false,

    start_date: new Date().toISOString(),
    end_date: null,
    use_compare_at_price: false,
    bundle_embed_id: generateRandomUniqueId(),
    allow_free_gifts: false,
    free_gifts: [],
    free_gift_text: "+ Free Gift",
    show_swatches: false,

    // target from
    targeting: {
        includeCustomers: "all",
        excludeCustomers: "none",
        includeCustomerData: "",
        excludeCustomerData: "",
        shopifyMarketsTargeting: "all",
        includeLocations: [],
        excludeLocations: [],
        shopifyMarketsTargetingData: [],
        //newKeys
        targetingType: "all",
        customerTagsEnabled: false,
        customerTagType: "include",
        customerTags: "",
        customerSpentEnabled: false,
        customerSpentType: "greater_than",
        customerSpent: "",
        customerCountriesEnabled: false,
        customerCountryType: "include",
        customerCountries: [],
    },

    // Settings
    general_settings: {
        show_bundle_on_product_page: false,
        show_multiple_bundles_on_product_page: false,
        redirect_after_adding_product_to_cart: "",
        redirect_to_checkout: false,
        show_powered_by_kefi: true,
        max_width: 1000,
        bundle_alignment: "flex-start",
        override_max_width: false,
        override_margin_top: false,
        margin_top: 20,
        // @ts-expect-error required
        add_to_cart_bg_color: "#303030",
        add_to_cart_border_radius: 8,
        add_to_cart_border_color: "#FFFFFF",
        add_to_cart_text_color: "#F5F5F5",
        add_to_cart_text_size: 14,
        add_to_cart_text_style: "750 normal",
    },
    bundles_settings: {
        enable_stock_control: false,
        out_of_stock_text: "Out of stock",
        listing_variant_with_image: false,
        work_only_with_widget: false,
        bundle_position: "below_cart",
        custom_selector: "",
    },
    show_bundle_widget: "show_everywhere",
    free_shipping: false,
    animation_add_to_cart_btn: "none",
};

const useBxgyHook = () => {
    const bundleConfigReducer = useCallback(
        (state: BxgyConfigInterface, action: Action<BxgyConfigInterface>): BxgyConfigInterface => {
            switch (action?.type) {
                case ChangeEvents.initState:
                    return { ...state, ...action.init };
                default:
                    if (action?.key && action.isComplex) {
                        return {
                            ...state,
                            [action?.key]: {
                                ...(state[action.key] as object),
                                ...(action.payload as object),
                            },
                        };
                    } else if (action?.key) return { ...state, [action?.key]: action.payload };
                    else return { ...state };
            }
        },
        [],
    );

    const [bundleConfig, dispatchBundleConfig] = useReducer(bundleConfigReducer, initialConfigData);

    console._log("bundleconfig in hook--,", bundleConfig);

    return {
        bundleConfig,
        dispatchBundleConfig,
    };
};

export default useBxgyHook;
